import MilestoneList from "../goals/MilestoneList";
import React from "react";
import { Card, Icon, Divider, Tooltip } from "@blueprintjs/core";
import Flexbox from "flexbox-react";
import Progress from "../../assets/Progress.svg";
import CurrentUserStore from "../../stores/CurrentUserStore";
import GoalProgressRow from "../goals/GoalProgressRow.js";
import "./MyAssessmentView.css";
import { isGoalProgressSpecialAccount } from "../../utils/goalProgressUtils";

const getDescriptionPreview = (description) => {
  if (!description) return "";
  const maxChars = 80;
  if (description.length <= maxChars) return description;
  return `${description.slice(0, maxChars)}... Read more`;
};

const GoalReadOnlyList = ({
  data,
  goalFinalRating,
  isMultiLevelReview = false,
  managerView = false,
  weightage = false,
  ratingLabels = [],
  subGoalsByGoalId = {},
  openSubGoals: controlledOpenSubGoals,
  toggleSubGoals: controlledToggleSubGoals
}) => {

  const [localOpenSubGoals, setLocalOpenSubGoals] = React.useState({});
  const openSubGoals = controlledOpenSubGoals || localOpenSubGoals;
  const toggleSubGoals = controlledToggleSubGoals || (id => {
    setLocalOpenSubGoals(previous => ({ ...previous, [id]: !previous[id] }));
  });

  const getSafeRatingValue = (value) => {

    // null / undefined
    if (value === null || value === undefined) {
      return "-";
    }

    // normal number
    if (typeof value === "number") {
      return value;
    }

    // normal string
    if (typeof value === "string") {
      return value.trim() || "-";
    }

    // object case
    if (typeof value === "object") {

      // current API structure
      if (
        value.start_range !== undefined &&
        value.end_range !== undefined
      ) {
        return `${value.start_range}-${value.end_range}`;
      }

      // common fallbacks
      if (value.rating !== undefined) {
        return value.rating;
      }

      if (value.value !== undefined) {
        return value.value;
      }

      if (value.label !== undefined) {
        return value.label;
      }

      // last fallback
      return "-";
    }

    return "-";
  };


  if (!data || data.length === 0) return null;
  return data?.map((goal, index) => {
    let rating = "-";

    if (!managerView) {

      rating = getSafeRatingValue(
        goal?.goal_score ??
        goal?.self_assessment_rating ??
        goal?.goalRating ??
        goal?.self_rating ??
        goal?.rating
      );
    } else {
      // MANAGER VIEW
      if (isMultiLevelReview) {
        rating = goal?.managers_goal_ratings?.rating || goal?.rating || "-";
      } else {
        rating = goal?.rating ?? "-";
      }
    }

    const accountId = CurrentUserStore?.activeAccount?.accountId ?? null;
    const subGoals = Array.isArray(goal.sub_goals) ? goal.sub_goals : (subGoalsByGoalId?.[goal.id] || []);
    const useRatingLabels = isGoalProgressSpecialAccount(accountId);
    const effectiveRatingLabels =
      useRatingLabels && (!Array.isArray(ratingLabels) || ratingLabels.length === 0)
        ? [
          { label: "Not Met", value: 1 },
          { label: "Partially Met", value: 2 },
          { label: "Met", value: 3 }
        ]
        : ratingLabels;

    const numericRating =
      typeof rating === "number" && !isNaN(rating)
        ? rating
        : null;

    const ratingLabel =
      numericRating === -1
        ? "NA"
        : (
          useRatingLabels &&
          Array.isArray(effectiveRatingLabels) &&
          effectiveRatingLabels.length > 0 &&
          numericRating !== null
        )
          ? (
            effectiveRatingLabels.find(
              rl => Number(rl.value) === Number(numericRating)
            )?.label ?? rating
          )
          : rating;

    return (
      <Flexbox key={goal?.id || index} flexDirection="column">
        <Flexbox justifyContent="space-between" className="assessment-answer-card" gap="20px">
          <Flexbox className="answer-container" flexGrow={1} flexDirection="column">
            <Flexbox className='push-10-t'>
              <span className="push-10-r" role="img" aria-label="chart">
                <Icon icon="settings" style={{ color: "#137CBD" }} />
              </span>
              <p className="assessment-question-text">{goal?.title}</p>
            </Flexbox>
            {goal?.cascade_id && (
              <div style={{ fontSize: 12, marginBottom: 8, color: "#667085" }}>
                Cascades from:
                <strong style={{ color: "#0052CC", marginLeft: 4 }}>{goal.cascade_title}</strong>
              </div>
            )}
            {goal?.description && (
              <Tooltip portalContainer={document.body}
                content={goal.description}
                placement="top"
                popoverClassName="goal-description-tooltip"
              >
                <div className="goal-sub-description" style={{ cursor: "pointer" }}>
                  {getDescriptionPreview(goal.description)}
                </div>
              </Tooltip>
            )}
            <Flexbox flexDirection="row" gap="16px" className="weitage-progres-row push-10-t" alignItems="center" style={{ flexWrap: "wrap" }}>
              {goal?.file_url &&
                <>

                  <Flexbox flexDirection="row" display="flex" alignItems="center" gap="8px">
                    <Tooltip portalContainer={document.body} content={goal?.file_name || "Attached File"} placement="top">
                      <div>
                        <a href={goal.file_url} target="_blank" rel="noopener noreferrer" style={{ color: "#106ba3", textDecoration: "underline", cursor: "pointer" }}>
                          <Icon icon="paperclip" size={12} />
                        </a>
                      </div>
                    </Tooltip>
                  </Flexbox>
                  <Divider className="push-10-l push-10-r question-divider" style={{ height: "20px" }} ></Divider>
                </>
              }
              <Tooltip portalContainer={document.body} content={"Progress"} placement="top">
                <img
                  src={Progress}
                  alt="progress icon"
                  style={{ width: 20, height: 20, marginRight: '5px', marginTop: '8px' }}
                />
              </Tooltip>
              <GoalProgressRow goal={goal} subGoals={subGoals} accountId={CurrentUserStore?.activeAccount?.accountId} />
            </Flexbox>
          </Flexbox>

          <Flexbox flexDirection="column" className="read-only-rating-container" style={{ marginTop: '5px' }} >
            <p className="answer-weightage">
              Rating: <strong>{ratingLabel ?? "-"}</strong>
            </p>
            {!!weightage &&
              <p className="answer-weightage">
                Weightage:{" "}
                {/* {goal?.goal_weight && */}
                <strong style={{ fontWeight: 600 }}>
                  {goal?.goal_weight ?? "-"}
                </strong>
                {/* } */}
              </p>}
          </Flexbox>
        </Flexbox>

        {(() => {
          return (
            <>
              <div
                style={{
                  marginTop: 8,
                  fontSize: 13,
                  color: "#6f5bd7",
                  cursor: subGoals.length ? "pointer" : "default",
                }}
                onClick={() => subGoals.length && goal?.id && toggleSubGoals(goal.id)}
              >
                <Icon
                  icon={goal?.id && openSubGoals?.[goal.id] ? "chevron-down" : "chevron-right"}
                  size={14}
                />
                {subGoals.length} Sub Goals
              </div>

              {subGoals.length > 0 && goal?.id && openSubGoals?.[goal.id] && (
                <div style={{ paddingLeft: 30, marginTop: 10 }}>
                  {subGoals?.map(sub => (
                    <Card
                      key={sub.id}
                      style={{
                        marginBottom: 10,
                        borderRadius: 8,
                        border: "1px solid " +
                          "#e5e5e5",
                        padding: 12,
                        boxShadow: "none"
                      }}
                    >
                      <div>
                        <b>{sub.title}</b>
                      </div>

                      {sub.description && (
                        <Tooltip portalContainer={document.body}
                          content={sub.description}
                          placement="top"
                          popoverClassName="goal-description-tooltip"
                        >
                          <div className="goal-sub-description" style={{ cursor: "pointer" }}>
                            {getDescriptionPreview(sub.description)}
                          </div>
                        </Tooltip>
                      )}
                      <Flexbox flexDirection="row" gap="16px" className="weitage-progres-row" alignItems="center">

                        {sub?.file_url &&
                          <>
                            <Flexbox flexDirection="row" display="flex" alignItems="center" gap="8px">
                              <Tooltip portalContainer={document.body} content={sub.file_name || "Attached File"} placement="top">
                                <div>
                                  <a href={sub.file_url} target="_blank" rel="noopener noreferrer" style={{ color: "#106ba3", textDecoration: "underline", cursor: "pointer" }}>
                                    <Icon icon="paperclip" size={12} />
                                  </a>
                                </div>
                              </Tooltip>
                              <Divider className="push-10-l push-10-r question-divider" style={{ height: "20px" }} ></Divider>

                            </Flexbox>
                          </>
                        }
                        <Tooltip portalContainer={document.body} content={"Progress"} placement="top">
                          <img
                            src={Progress}
                            alt="progress icon"
                            style={{ width: 20, height: 20, marginRight: '5px', marginTop: '4px' }}
                          />
                        </Tooltip>
                        <GoalProgressRow goal={sub} accountId={CurrentUserStore?.activeAccount?.accountId} />
                      </Flexbox>

                      <MilestoneList subGoal={sub} editable={false} />
                    </Card>
                  ))}
                </div>
              )}
            </>
          );
        })()}

        {index !== data.length - 1 && <Divider className="question-divider" />}
      </Flexbox>)
  }
  );
};

export default GoalReadOnlyList;

